import { useEffect, useMemo, useState } from 'react';
import { ACCOUNT_TASK_COMPLETED_EVENT } from '@/components/activity/AccountCapabilityLauncher';
import { supabase } from './supabase';

/**
 * « La chaîne des comptes » (21/10/2026): a task rarely ends in Domino — after
 * each action the planner proposes the next one on the same task. What was
 * done is the task's action runs (`action_runs`, read under the Thread's RLS),
 * each with the label of the proposal it ran and where its result lives.
 */
export const DONE_RUN_STATUSES = ['succeeded', 'partially_succeeded', 'failed'] as const;

export interface WorkRun {
  id: string;
  topicId: string;
  threadId: string;
  status: (typeof DONE_RUN_STATUSES)[number];
  proposalId: string;
  /** What was run, as its button said. */
  label: string;
  requesterUserId: string;
  finishedAt: string;
  /** `flow_execution:<id>`, `task_action_run:<id>`, `action_run:<id>`. */
  resultRef: string | null;
}

type RunRow = {
  id: string; topic_id: string; thread_id: string; status: WorkRun['status']; proposal_id: string; requester_user_id: string;
  completed_at: string | null; updated_at: string;
  proposal: { cta_label: string } | null;
  outcomes: Array<{ result_view_ref: string | null }> | null;
};

const RUN_COLUMNS = 'id,topic_id,thread_id,status,proposal_id,requester_user_id,completed_at,updated_at,proposal:thread_action_proposals(cta_label),outcomes:action_outcomes(result_view_ref)';

const toRun = (row: RunRow): WorkRun => ({
  id: row.id, topicId: row.topic_id, threadId: row.thread_id, status: row.status, proposalId: row.proposal_id,
  label: row.proposal?.cta_label ?? 'Action', requesterUserId: row.requester_user_id,
  finishedAt: row.completed_at ?? row.updated_at, resultRef: row.outcomes?.[0]?.result_view_ref ?? null,
});

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The finished runs of these tasks, oldest first; legacy rows (not Thread topics) have none. */
export const loadTopicRuns = async (topicIds: string[]): Promise<WorkRun[]> => {
  const ids = topicIds.filter((id) => UUID.test(id));
  if (ids.length === 0) return [];
  const { data, error } = await supabase.from('action_runs').select(RUN_COLUMNS)
    .in('topic_id', ids).in('status', [...DONE_RUN_STATUSES]).order('completed_at', { ascending: true });
  if (error) throw new Error('Les actions faites ne sont pas accessibles pour le moment.');
  return ((data ?? []) as unknown as RunRow[]).map(toRun);
};

/** Runs I launched that finished since `since` (today's done work). */
export const loadMyRunsSince = async (since: Date): Promise<WorkRun[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase.from('action_runs').select(RUN_COLUMNS)
    .eq('requester_user_id', user.id).in('status', [...DONE_RUN_STATUSES])
    .gte('completed_at', since.toISOString()).order('completed_at', { ascending: true });
  if (error) throw new Error('Les actions faites ne sont pas accessibles pour le moment.');
  return ((data ?? []) as unknown as RunRow[]).map(toRun);
};

export interface RunContext {
  label: string;
  accountSubjectId: string | null;
  flowId: string | null;
}

const APP_WORKSPACE_KEY = /^app:([0-9a-f-]{36}):/;

/** What each Thread is about (an account or an app), for runs whose task left the file. */
export const loadRunContexts = async (threadIds: string[]): Promise<Record<string, RunContext>> => {
  if (threadIds.length === 0) return {};
  const { data: threads, error } = await supabase.from('work_threads').select('id,account_subject_id,workspace_key').in('id', threadIds);
  if (error) throw new Error('Les actions faites ne sont pas accessibles pour le moment.');
  const rows = (threads ?? []) as Array<{ id: string; account_subject_id: string | null; workspace_key: string | null }>;
  const subjectIds = [...new Set(rows.flatMap((row) => (row.account_subject_id ? [row.account_subject_id] : [])))];
  const flowIds = [...new Set(rows.flatMap((row) => APP_WORKSPACE_KEY.exec(row.workspace_key ?? '')?.[1] ?? []))];
  const [subjects, flows] = await Promise.all([
    subjectIds.length ? supabase.from('activity_subjects').select('id,display_name').in('id', subjectIds) : Promise.resolve({ data: [] }),
    flowIds.length ? supabase.from('flows').select('id,title').in('id', flowIds) : Promise.resolve({ data: [] }),
  ]);
  const names = new Map(((subjects.data ?? []) as Array<{ id: string; display_name: string }>).map((row) => [row.id, row.display_name]));
  const titles = new Map(((flows.data ?? []) as Array<{ id: string; title: string }>).map((row) => [row.id, row.title]));
  return Object.fromEntries(rows.map((row) => {
    const flowId = APP_WORKSPACE_KEY.exec(row.workspace_key ?? '')?.[1] ?? null;
    return [row.id, {
      label: (row.account_subject_id ? names.get(row.account_subject_id) : flowId ? titles.get(flowId) : null) ?? 'Travail',
      accountSubjectId: row.account_subject_id, flowId,
    }];
  }));
};

/** Where to read a run's result: the run itself for a tool, otherwise its account or its app. */
export const runResultHref = (run: Pick<WorkRun, 'resultRef'>, context: Pick<RunContext, 'accountSubjectId' | 'flowId'> | null): string | null => {
  const [kind, id] = (run.resultRef ?? '').split(':');
  if (kind === 'flow_execution' && id) return `/executions/${encodeURIComponent(id)}`;
  if (context?.accountSubjectId) {
    return `/threads/${encodeURIComponent(context.accountSubjectId)}${kind === 'task_action_run' && id ? `#task-action-${id}` : ''}`;
  }
  return context?.flowId ? `/tools/${encodeURIComponent(context.flowId)}` : null;
};

/** Midnight today, in the person's calendar. */
export const startOfToday = (now: Date = new Date()): Date => new Date(now.getFullYear(), now.getMonth(), now.getDate());

/**
 * The file's done work: the last finished run of each task in the file, and
 * my runs finished today whose task left the file (they stay until tomorrow).
 */
export const useFileRuns = (topicIds: string[]) => {
  const key = [...topicIds].sort().join(',');
  const [lastByTopic, setLastByTopic] = useState<Map<string, WorkRun>>(new Map());
  const [today, setToday] = useState<Array<WorkRun & { context: RunContext | null }>>([]);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    const reload = () => setReloadKey((value) => value + 1);
    window.addEventListener(ACCOUNT_TASK_COMPLETED_EVENT, reload);
    return () => window.removeEventListener(ACCOUNT_TASK_COMPLETED_EVENT, reload);
  }, []);
  useEffect(() => {
    let active = true;
    const ids = key ? key.split(',') : [];
    void Promise.all([loadTopicRuns(ids), loadMyRunsSince(startOfToday())])
      .then(async ([runs, mine]) => {
        const last = new Map<string, WorkRun>();
        runs.forEach((run) => last.set(run.topicId, run));
        const inFile = new Set(ids);
        const gone = mine.filter((run) => !inFile.has(run.topicId));
        const contexts = await loadRunContexts([...new Set(gone.map((run) => run.threadId))]);
        if (!active) return;
        setLastByTopic(last);
        setToday(gone.map((run) => ({ ...run, context: contexts[run.threadId] ?? null })));
      })
      .catch((cause) => console.error('file_runs_load_failed', cause));
    return () => { active = false; };
  }, [key, reloadKey]);
  return useMemo(() => ({ lastByTopic, today }), [lastByTopic, today]);
};
